/* Shared presentational pieces for the appraisal module.

   The same idea as GrievancePieces: one place for the small repeated bits, so
   spacing and wording stay consistent instead of being reinvented per panel.

   The piece that matters most here is StatusGuidance. A status pill tells you
   where a record is; it does not tell you whose turn it is or what happens
   next, and appraisal has fifteen statuses across two audiences. Grievance
   solved this with a plain-language banner under the header, so this follows
   that pattern rather than inventing another one. */
import { cn } from "../../utils/tailwindUtils";

/* Keyed by status, then by who is looking. Written from the reader's side:
   an employee is told what is being asked of them, a manager what they are
   waiting on or owe. A status missing for one role falls back to the shared
   line, and an unknown status renders nothing rather than a wrong guess. */
const GUIDANCE = {
    draft: {
        any: ["Not yet assigned", "This appraisal has not been handed to the employee."],
    },
    pending_goal_agreement: {
        employee: ["Agree your goals", "Read the KPIs and weightages below, then accept them or send them back with a reason."],
        rm: ["Waiting on the employee", "They can accept these goals or send them back. Self-evaluation opens once they are agreed."],
    },
    goals_sent_back: {
        employee: ["Sent back to your manager", "They will revise the goals and return them for you to agree."],
        rm: ["Goals need revisiting", "The employee has asked for changes. Revise the set below and re-send it."],
    },
    goals_agreed: {
        employee: ["Goals agreed", "Nothing to do for now. Your self-evaluation opens on the date set for this cycle."],
        rm: ["Goals agreed", "Self-evaluation opens automatically when the cycle reaches that date."],
    },
    pending_employee_submission: {
        employee: ["Your self-evaluation is open", "Rate every KPI and submit. You can save a draft as often as you like."],
        rm: ["Waiting on the employee", "They are completing their self-evaluation."],
    },
    in_progress: {
        employee: ["Self-evaluation in progress", "Finish rating every KPI, then submit for review."],
        rm: ["Waiting on the employee", "They have started their self-evaluation but not submitted it."],
    },
    submitted_to_rm: {
        employee: ["With your manager", "You cannot edit it now unless your manager sends it back."],
        rm: ["Your review is needed", "Rate each KPI, set potential if you have a view, then approve or send it back."],
    },
    sent_back_by_rm: {
        employee: ["Sent back for revision", "Your manager has asked for changes. Update your answers and resubmit."],
        rm: ["Sent back to the employee", "Waiting for them to revise and resubmit."],
    },
    rm_reviewed: {
        any: ["Reviewed by the manager", "Moving on to HR."],
    },
    pending_hr_approval: {
        employee: ["With HR", "Your manager has reviewed it and HR is checking it over."],
        rm: ["With HR", "Your review is done. HR approves before anything is published."],
    },
    sent_back_by_hr: {
        employee: ["Sent back by HR", "HR has asked for changes before this can be approved."],
        rm: ["Sent back by HR", "HR has asked for changes before this can be approved."],
    },
    hr_approved: {
        employee: ["Approved, not yet published", "You will see the result once HR publishes it."],
        rm: ["Approved by HR", "Awaiting publication. It may still go through calibration first."],
    },
    published: {
        any: ["Published", "The result is final and the record is locked. Development actions stay editable."],
    },
    closed: {
        any: ["Closed", "This cycle is finished."],
    },
    overdue: {
        employee: ["Overdue", "The self-evaluation window has closed and nothing was submitted. Speak to your manager."],
        rm: ["Overdue", "The employee did not submit before the window closed."],
    },
};

/**
 * Plain-language banner saying whose turn it is.
 *
 * `role` is "employee" or "rm"; anything else gets the shared wording.
 * Renders nothing for a status it does not know, so a new status shows no
 * guidance rather than misleading guidance.
 */
export const StatusGuidance = ({ status, role, className }) => {
    const entry = GUIDANCE[status];
    if (!entry) return null;
    const copy = entry[role] || entry.any;
    if (!copy) return null;
    const [title, detail] = copy;
    return (
        <div className={cn(
            "flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3.5",
            "dark:border-gray-700 dark:bg-gray-800/40", className,
        )}>
            <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">{title}</p>
                <p className="text-xs text-muted-foreground">{detail}</p>
            </div>
        </div>
    );
};

/** Small uppercase heading above a block, matching SectionLabel in grievance. */
export const SectionLabel = ({ children, className }) => (
    <p className={cn(
        "mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground",
        className,
    )}>
        {children}
    </p>
);

/** Label above a value, for the read-only detail rows the panels repeat. */
export const Field = ({ label, children, className }) => (
    <div className={cn("min-w-0", className)}>
        <p className="text-xs text-muted-foreground">{label}</p>
        <div className="text-sm">{children ?? "—"}</div>
    </div>
);

export default StatusGuidance;
